import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

const DepartmentWiseProjectsChart = ({ data = [] }) => {
    const deptChartRef = useRef(null);
    const deptChartInstance = useRef(null);

    const defaultData = [
        { department: 'CSE', projects: 90, avg_duration: 20 },
        { department: 'ME', projects: 60, avg_duration: 18 },
        { department: 'EE', projects: 50, avg_duration: 25 },
        { department: 'ECE', projects: 60, avg_duration: 16 },
        { department: 'Civil', projects: 75, avg_duration: 22 },
        { department: 'Chemical', projects: 20, avg_duration: 12 },
    ];

    // Use provided data or default - show ALL departments
    const chartData = data.length > 0 ? data : defaultData;

    const labels = chartData.map(item => item.department || item.label);
    const projectsData = chartData.map(item => item.projects || item.count || 0);
    const durationData = chartData.map(item => item.avg_duration || item.avgDuration || 0);

    const maxVal = Math.max(...projectsData, ...durationData, 0);
    const suggestedMax = maxVal === 0 ? 10 : Math.ceil(maxVal * 1.15);

    useEffect(() => {
        if (!deptChartRef.current) return;

        if (deptChartInstance.current) {
            deptChartInstance.current.destroy();
        }

        const ctx = deptChartRef.current.getContext('2d');
        if (!ctx) return;

        deptChartInstance.current = new ChartJS(ctx, {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [
                    {
                        label: 'Projects',
                        data: projectsData,
                        backgroundColor: '#b8d4f0', // Light blue matching Figma
                        borderColor: '#8bb8e8',
                        borderWidth: 1,
                        borderRadius: 2,
                        barThickness: 12,
                    },
                    {
                        label: 'Avg. Duration (In Weeks)',
                        data: durationData,
                        backgroundColor: '#f0b8d4', // Pink matching Figma
                        borderColor: '#e88bb8',
                        borderWidth: 1,
                        borderRadius: 2,
                        barThickness: 12,
                    },
                ],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                categoryPercentage: 0.8,
                barPercentage: 0.9,
                scales: {
                    x: {
                        beginAtZero: true,
                        suggestedMax: suggestedMax,
                        ticks: {
                            precision: 0,
                            font: { size: 12, family: 'Arial, sans-serif' },
                            color: '#666666',
                        },
                        grid: { color: '#f0f0f0', drawBorder: false },
                        border: { display: false },
                        title: {
                            display: true,
                            text: 'Years',
                            color: '#333333',
                            font: { size: 12, family: 'Arial, sans-serif', weight: '500' },
                        },
                    },
                    y: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: {
                            font: { size: 12, family: 'Arial, sans-serif' },
                            color: '#333333',
                        },
                        title: {
                            display: true,
                            text: 'No. of Users',
                            color: '#333333',
                            font: { size: 12, family: 'Arial, sans-serif', weight: '500' },
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom', // Legend at bottom like Figma
                        align: 'center',
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12,
                            padding: 15,
                            font: { size: 11, family: 'Arial, sans-serif' },
                            color: '#333333',
                        },
                    },
                    tooltip: { enabled: true },
                    datalabels: { display: false },
                },
            },
        });

        return () => {
            if (deptChartInstance.current) {
                deptChartInstance.current.destroy();
            }
        };
    }, [data, labels, projectsData, durationData, suggestedMax]);

    const hasData = chartData.some(item =>
        (item.projects || item.count || 0) > 0 ||
        (item.avg_duration || item.avgDuration || 0) > 0
    );

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            {!hasData && data.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <Typography sx={{ color: '#999', fontSize: '14px' }}>
                        No department data available
                    </Typography>
                </Box>
            ) : (
                <canvas ref={deptChartRef} style={{ width: '100%', height: '100%', display: 'block' }} />
            )}
        </Box>
    );
};

export default DepartmentWiseProjectsChart;
